import { useState, useEffect } from "react";
import { HeroLineChart, Icon, MONTHS_ABBR, MonthPills, NetworkDonut, SectionHeader, api, naira, providerMeta, relTime } from "../lib/core";

export const AnalyticsOverviewTab = ({ data, months, selected, onSelectMonth, onNavigate, onOpenBeneficiaries, onOpenWallet }) => {
  const ref = data.referral || {};
  const maxActivity = Math.max(...(data.top_up_activity || []).map((d) => d.count), 1);
  return (
    <div className="space-y-6" data-testid="analytics-overview-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg shadow-red-900/20" style={{ background: "linear-gradient(135deg,#B91C1C 0%,#7F1D1D 100%)" }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/70 text-sm">Total Spent · {data.month_label} <span className="text-white/90 font-semibold">{data.month_pct}%</span></p>
            <p className="text-4xl font-extrabold mt-1" data-testid="overview-total-spent">{naira(data.total_spent)}</p>
            <p className="text-white/70 text-sm mt-0.5">Airtime + Data combined</p>
          </div>
          <span className="flex items-center gap-1 bg-white/15 rounded-full px-2.5 py-1 text-xs font-semibold">
            {data.spend_trend_available ? (
              <>
                <Icon name={data.spend_trend_pct >= 0 ? "arrow_upward" : "arrow_downward"} className="text-sm" />
                {data.spend_trend_pct >= 0 ? "+" : ""}{data.spend_trend_pct}% vs prev
              </>
            ) : "New this month"}
          </span>
        </div>
        <div className="mt-2">
          <HeroLineChart points={data.spend_line || []} />
          <div className="flex justify-between text-[11px] text-white/70 px-1 mt-1">
            {(data.spend_line || []).map((d) => <span key={d.day}>{d.day}</span>)}
          </div>
        </div>
        <div className="flex gap-3 mt-3">
          <div className="flex-1 bg-white/12 rounded-xl py-2.5 px-3 flex items-center gap-2 justify-center">
            <Icon name="bar_chart" className="text-white/80 text-base" />
            <span className="text-sm text-white/80">Data</span>
            <span className="font-bold ml-auto">{naira(data.data_spent)}</span>
          </div>
          <div className="flex-1 bg-white/12 rounded-xl py-2.5 px-3 flex items-center gap-2 justify-center">
            <Icon name="call" className="text-white/80 text-base" />
            <span className="text-sm text-white/80">Airtime</span>
            <span className="font-bold ml-auto">{naira(data.airtime_spent)}</span>
          </div>
        </div>
      </div>

      <MonthPills months={months} selected={selected} onSelect={onSelectMonth} />

      {/* Network Usage donut */}
      <div>
        <SectionHeader title="Network Usage" action="Details" onAction={() => onNavigate("transactions")} testid="network-usage-details" />
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4">
          <NetworkDonut segments={data.network_breakdown || []} total={data.total_spent} />
          <div className="flex-1 space-y-2.5">
            {(data.network_breakdown || []).length === 0 ? (
              <p className="text-slate-400 text-sm">No network usage this month.</p>
            ) : (
              (data.network_breakdown || []).map((n) => (
                <div key={n.network} className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: providerMeta(n.network).color }} />
                  <span className="font-medium text-slate-700 w-16">{n.network}</span>
                  <span className="font-bold text-slate-900 ml-auto">{n.pct}% · {naira(n.amount)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Top-up Activity */}
      <div>
        <SectionHeader title="Top-up Activity" />
        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <div className="flex items-end justify-between gap-2 h-36" data-testid="topup-activity-chart">
            {(data.top_up_activity || []).map((d) => {
              const isMax = d.count === maxActivity && d.count > 0;
              const h = (d.count / maxActivity) * 100;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                  {isMax && <span className="text-xs font-bold bg-white border border-slate-200 rounded-md px-1.5 py-0.5 shadow-sm">{d.count}</span>}
                  <div className={`w-full rounded-lg ${isMax ? "bg-primary-custom" : "bg-slate-200"}`} style={{ height: `${Math.max(h, 4)}%` }} />
                  <span className="text-xs text-slate-500">{d.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Groups Analytics */}
      <div>
        <SectionHeader title="Groups Analytics" action="Manage" onAction={onOpenBeneficiaries} testid="groups-manage" />
        {(data.groups || []).length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center text-slate-400 text-sm">No groups yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(data.groups || []).slice(0, 3).map((g) => (
              <div key={g.group_id} className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`group-card-${g.group_name}`}>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><Icon name="group" className="text-slate-500 text-base" /></span>
                  <span className="text-sm text-slate-500 truncate">{g.group_name}</span>
                </div>
                <p className="font-bold text-slate-900 text-lg mt-2">{naira(g.total_spent)}</p>
                <p className="text-xs text-slate-400">{g.member_count} member{g.member_count === 1 ? "" : "s"}</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-custom rounded-full" style={{ width: `${g.pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-500">{g.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referral Earnings */}
      <div>
        <SectionHeader title="Referral Earnings" action="See all" onAction={() => onNavigate("referral")} testid="referral-see-all-analytics" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="referral-earned-card">
            <div className="w-9 h-9 rounded-lg bg-primary-custom/10 flex items-center justify-center mb-2"><Icon name="account_balance_wallet" className="text-primary-custom text-lg" /></div>
            <p className="font-extrabold text-slate-900 text-xl">{naira(ref.total_earned)}</p>
            <p className="text-xs text-slate-400">Total Earned</p>
            {ref.earned_today > 0 && <span className="inline-block mt-2 text-[11px] font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">+{naira(ref.earned_today)} today</span>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="referral-active-card">
            <div className="w-9 h-9 rounded-lg bg-primary-custom/10 flex items-center justify-center mb-2"><Icon name="groups" className="text-primary-custom text-lg" /></div>
            <p className="font-extrabold text-slate-900 text-xl">{ref.active_referrals}</p>
            <p className="text-xs text-slate-400">Active Referrals</p>
            <p className="text-[11px] text-slate-400 mt-2">of {ref.goal} goal</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="referral-conversion-card">
            <div className="w-9 h-9 rounded-lg bg-primary-custom/10 flex items-center justify-center mb-2"><Icon name="percent" className="text-primary-custom text-lg" /></div>
            <p className="font-extrabold text-slate-900 text-xl">{ref.conversion_rate}%</p>
            <p className="text-xs text-slate-400">Conversion Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- BREAKDOWN TAB (design 2) ----------

export const AnalyticsBreakdownTab = ({ data, months, selected, onSelectMonth, onNavigate, onOpenBeneficiaries }) => {
  const maxNet = Math.max(...(data.network_breakdown || []).map((n) => n.amount), 1);
  return (
    <div className="space-y-6" data-testid="analytics-breakdown-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg shadow-red-900/20" style={{ background: "linear-gradient(135deg,#7F1D1D 0%,#B91C1C 100%)" }}>
        <div className="flex items-start justify-between">
          <p className="text-white/70 text-sm">Total Spent This Month</p>
          <span className="bg-white/15 rounded-full px-2.5 py-1 text-xs font-semibold">{data.month_label} {data.month_pct}%</span>
        </div>
        <p className="text-4xl font-extrabold mt-1" data-testid="breakdown-total-spent">{naira(data.total_spent)}</p>
        <span className={`inline-flex items-center gap-1 mt-2 rounded-full px-2.5 py-1 text-xs font-semibold ${data.spend_trend_available ? "bg-green-500 text-white" : "bg-white/15 text-white"}`}>
          {data.spend_trend_available ? (
            <>{data.spend_trend_pct >= 0 ? "+" : ""}{data.spend_trend_pct}% vs last month <Icon name="arrow_outward" className="text-sm" /></>
          ) : "New this month"}
        </span>
        <div className="mt-3">
          <HeroLineChart points={data.spend_line || []} />
          <div className="flex justify-between text-[11px] text-white/70 px-1 mt-1">
            {(data.spend_line || []).map((d) => <span key={d.day}>{d.day}</span>)}
          </div>
        </div>
        <div className="flex gap-3 mt-3">
          <div className="flex-1 bg-white/12 rounded-xl py-2.5 px-3 text-center text-sm">Data: <span className="font-bold">{naira(data.data_spent)}</span></div>
          <div className="flex-1 bg-white/12 rounded-xl py-2.5 px-3 text-center text-sm flex items-center justify-center gap-1.5"><Icon name="smartphone" className="text-white/80 text-base" />Airtime: <span className="font-bold">{naira(data.airtime_spent)}</span></div>
        </div>
      </div>

      <MonthPills months={months} selected={selected} onSelect={onSelectMonth} />

      {/* Spending Breakdown */}
      <div>
        <SectionHeader title="Spending Breakdown" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="airtime-spent-card">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-primary-custom/10 flex items-center justify-center"><Icon name="call" className="text-primary-custom" /></span>
              <div>
                <p className="font-extrabold text-slate-900 text-xl">{naira(data.airtime_spent)}</p>
                <p className="text-xs text-slate-500">Airtime Spent</p>
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-primary-custom rounded-full" style={{ width: `${data.airtime_pct}%` }} /></div>
            <p className="text-xs text-slate-400 mt-1">{data.airtime_pct}% of total</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="data-spent-card">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-primary-custom/10 flex items-center justify-center"><Icon name="wifi" className="text-primary-custom" /></span>
              <div>
                <p className="font-extrabold text-slate-900 text-xl">{naira(data.data_spent)}</p>
                <p className="text-xs text-slate-500">Data Spent</p>
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-primary-custom rounded-full" style={{ width: `${data.data_pct}%` }} /></div>
            <p className="text-xs text-slate-400 mt-1">{data.data_pct}% of total</p>
          </div>
        </div>
      </div>

      {/* Network Breakdown */}
      <div>
        <SectionHeader title="Network Breakdown" action="Details" onAction={() => onNavigate("transactions")} testid="network-breakdown-details" />
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          {(data.network_breakdown || []).length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-4">No network usage this month.</p>
          ) : (
            (data.network_breakdown || []).map((n) => (
              <div key={n.network} className="flex items-center gap-3" data-testid={`net-row-${n.network}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: providerMeta(n.network).color }} />
                <span className="font-medium text-slate-700 w-20 text-sm">{n.network}</span>
                <div className="flex-1 h-4 bg-red-50 rounded-full overflow-hidden relative">
                  <div className="h-full bg-primary-custom/30 rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max((n.amount / maxNet) * 100, 12)}%` }}>
                    <span className="text-[10px] font-bold text-primary-custom">{n.pct}%</span>
                  </div>
                </div>
                <span className="font-bold text-slate-900 text-sm w-16 text-right">{naira(n.amount)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Group Top-ups */}
      <div>
        <SectionHeader title="Group Top-ups" action="Manage" onAction={onOpenBeneficiaries} testid="group-topups-manage" />
        {(data.groups || []).length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center text-slate-400 text-sm">No groups yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(data.groups || []).slice(0, 2).map((g, i) => (
              <div key={g.group_id} className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`group-topup-${g.group_name}`}>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><Icon name={i === 0 ? "group" : "work"} className="text-slate-500 text-base" /></span>
                  <span className="text-sm font-medium text-slate-700 truncate flex-1">{g.group_name}</span>
                  {i === 0 && g.total_spent > 0 && <span className="text-[10px] font-semibold text-primary-custom flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary-custom" />Active</span>}
                </div>
                <p className="font-extrabold text-primary-custom text-xl mt-2">{naira(g.total_spent)}</p>
                <p className="text-xs text-slate-400">{g.member_count} member{g.member_count === 1 ? "" : "s"} · {g.recharge_count} top-up{g.recharge_count === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div>
        <SectionHeader title="Recent Transactions" action="View all" onAction={() => onNavigate("transactions")} testid="recent-tx-view-all" />
        {(data.recent_transactions || []).length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400">
            <Icon name="receipt_long" className="text-4xl" />
            <p className="mt-2 text-sm">No transactions yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
            {(data.recent_transactions || []).map((t) => {
              const meta = providerMeta(t.provider);
              return (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`recent-tx-${t.id}`}>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0" style={{ backgroundColor: meta.color, color: meta.textColor }}>
                    {(t.provider || "?").charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{t.phone}</p>
                    <p className="text-xs text-slate-400 capitalize">{t.provider} · {t.service_type}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-900">{naira(t.amount)}</p>
                    <p className="text-xs text-slate-400">{relTime(t.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- ANALYTICS PAGE (tabs container) ----------

export const AnalyticsPage = ({ onNavigate, onOpenBeneficiaries, onOpenWallet }) => {
  const now = new Date();
  const year = now.getFullYear();
  const months = MONTHS_ABBR.map((label, i) => ({ key: `${year}-${String(i + 1).padStart(2, "0")}`, label }));
  const [tab, setTab] = useState("overview");
  const [selected, setSelected] = useState(`${year}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true); setLoadError(false);
    api.get(`/analytics/overview?month=${selected}`)
      .then((res) => { if (active) setData(res.data); })
      .catch((err) => { console.error("analytics fetch failed", err); if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selected, reloadKey]);

  return (
    <div className="max-w-4xl" data-testid="analytics-page">
      {/* Tab switcher */}
      <div className="flex items-center justify-between mb-5">
        <div className="inline-flex bg-white border border-slate-200 rounded-full p-1" data-testid="analytics-tabs">
          <button
            onClick={() => setTab("overview")}
            data-testid="analytics-tab-overview"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${tab === "overview" ? "bg-primary-custom text-white" : "text-slate-500"}`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab("breakdown")}
            data-testid="analytics-tab-breakdown"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${tab === "breakdown" ? "bg-primary-custom text-white" : "text-slate-500"}`}
          >
            Breakdown
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
        </div>
      ) : loadError || !data ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="analytics-error">
          <Icon name="cloud_off" className="text-slate-300 text-5xl" />
          <p className="mt-3 text-slate-600 font-semibold">Couldn't load analytics</p>
          <p className="text-sm text-slate-400 mt-1">Please check your connection and try again.</p>
          <button onClick={() => setReloadKey((k) => k + 1)} data-testid="analytics-retry"
            className="mt-4 bg-primary-custom text-white font-semibold px-5 py-2.5 rounded-xl">Retry</button>
        </div>
      ) : tab === "overview" ? (
        <AnalyticsOverviewTab data={data} months={months} selected={selected} onSelectMonth={setSelected} onNavigate={onNavigate} onOpenBeneficiaries={onOpenBeneficiaries} onOpenWallet={onOpenWallet} />
      ) : (
        <AnalyticsBreakdownTab data={data} months={months} selected={selected} onSelectMonth={setSelected} onNavigate={onNavigate} onOpenBeneficiaries={onOpenBeneficiaries} />
      )}
    </div>
  );
};

// ============================================================
// REFERRAL PROGRAM — Invite Friends + Referral Rewards pages
// ============================================================

